import { useState, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function ScoreInput({ player1Name, player2Name, player1Id, player2Id, onScoreChange }) {
  const [sets, setSets] = useState([
    { p1: "", p2: "" },
    { p1: "", p2: "" },
    { p1: "", p2: "" },
  ]);
  const cbRef = useRef(onScoreChange);
  cbRef.current = onScoreChange;

  const computeAndNotify = useCallback((newSets) => {
    const setsWon = { p1: 0, p2: 0 };
    const validSets = [];
    for (const s of newSets) {
      const a = parseInt(s.p1);
      const b = parseInt(s.p2);
      if (isNaN(a) || isNaN(b)) continue;
      validSets.push(`${a}-${b}`);
      if (a > b) setsWon.p1++;
      else if (b > a) setsWon.p2++;
    }
    const score = validSets.join(", ");
    let winnerId = null;
    if (setsWon.p1 >= 2) winnerId = player1Id;
    else if (setsWon.p2 >= 2) winnerId = player2Id;
    cbRef.current({ score, winnerId, setsWon, validSets });
  }, [player1Id, player2Id]);

  const updateSet = (setIdx, player, value) => {
    const val = value.replace(/\D/g, "").slice(0, 1);
    const num = parseInt(val);
    if (val && (num < 0 || num > 7)) return;
    setSets((prev) => {
      const next = [...prev];
      next[setIdx] = { ...next[setIdx], [player]: val };
      computeAndNotify(next);
      return next;
    });
  };

  const p1SetsWon = sets.filter((s) => parseInt(s.p1) > parseInt(s.p2) && s.p1 !== "" && s.p2 !== "").length;
  const p2SetsWon = sets.filter((s) => parseInt(s.p2) > parseInt(s.p1) && s.p1 !== "" && s.p2 !== "").length;
  const matchDecided = p1SetsWon >= 2 || p2SetsWon >= 2;

  return (
    <div className="space-y-4" data-testid="score-input">
      {/* Players Header */}
      <div className="grid grid-cols-[1fr_auto_auto_auto_1fr] gap-2 items-center">
        <div />
        <span className="text-xs uppercase tracking-wider text-gray-500 text-center w-16">Set 1</span>
        <span className="text-xs uppercase tracking-wider text-gray-500 text-center w-16">Set 2</span>
        <span className="text-xs uppercase tracking-wider text-gray-500 text-center w-16">Set 3</span>
        <div />
      </div>

      {/* Player 1 Row */}
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold truncate ${p1SetsWon >= 2 ? "text-[#CCFF00]" : "text-white"}`}>
            {player1Name}
          </span>
          {p1SetsWon >= 2 && <Badge className="bg-[#CCFF00] text-black text-[10px] px-1.5 py-0">W</Badge>}
        </div>
        {[0, 1, 2].map((si) => (
          <Input
            key={si}
            data-testid={`set-${si + 1}-p1`}
            value={sets[si].p1}
            onChange={(e) => updateSet(si, "p1", e.target.value)}
            disabled={si === 2 && matchDecided && !(p1SetsWon === 1 && p2SetsWon === 1)}
            className="w-16 h-10 text-center bg-[#0A0A0A] border-white/20 text-white text-lg font-['JetBrains_Mono'] font-bold focus:border-[#CCFF00] disabled:opacity-30"
            maxLength={1}
          />
        ))}
        <span className="font-['Barlow_Condensed'] text-xl font-black text-gray-500 w-8 text-center">{p1SetsWon}</span>
      </div>

      {/* Player 2 Row */}
      <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold truncate ${p2SetsWon >= 2 ? "text-[#CCFF00]" : "text-white"}`}>
            {player2Name}
          </span>
          {p2SetsWon >= 2 && <Badge className="bg-[#CCFF00] text-black text-[10px] px-1.5 py-0">W</Badge>}
        </div>
        {[0, 1, 2].map((si) => (
          <Input
            key={si}
            data-testid={`set-${si + 1}-p2`}
            value={sets[si].p2}
            onChange={(e) => updateSet(si, "p2", e.target.value)}
            disabled={si === 2 && matchDecided && !(p1SetsWon === 1 && p2SetsWon === 1)}
            className="w-16 h-10 text-center bg-[#0A0A0A] border-white/20 text-white text-lg font-['JetBrains_Mono'] font-bold focus:border-[#CCFF00] disabled:opacity-30"
            maxLength={1}
          />
        ))}
        <span className="font-['Barlow_Condensed'] text-xl font-black text-gray-500 w-8 text-center">{p2SetsWon}</span>
      </div>

      {/* Match Status */}
      {matchDecided && (
        <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 p-3 text-center" data-testid="match-decided">
          <span className="text-[#CCFF00] font-bold text-sm uppercase tracking-wider">
            Ganador: {p1SetsWon >= 2 ? player1Name : player2Name}
          </span>
        </div>
      )}

      {/* Score Format Info */}
      <p className="text-xs text-gray-600 text-center">3 sets a 6 juegos (0-7 por set)</p>
    </div>
  );
}

export function ScoreDisplay({ score }) {
  if (!score || score === "BYE") return <span className="text-gray-600 text-xs">-</span>;

  const sets = score.split(",").map((s) => s.trim());
  return (
    <div className="flex gap-1.5 items-center" data-testid="score-display">
      {sets.map((set, i) => {
        const [p1, p2] = set.split("-").map(Number);
        return (
          <span key={i} className="font-['JetBrains_Mono'] text-xs">
            <span className={p1 > p2 ? "text-[#CCFF00] font-bold" : "text-gray-400"}>{set.split("-")[0]}</span>
            <span className="text-gray-600">-</span>
            <span className={p2 > p1 ? "text-[#CCFF00] font-bold" : "text-gray-400"}>{set.split("-")[1]}</span>
          </span>
        );
      })}
    </div>
  );
}
